import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  Calendar, Clock, Eye, Share2, Facebook, Twitter, 
  Linkedin, Link as LinkIcon, Bookmark, BookmarkCheck,
  ChevronLeft, ChevronRight, MessageCircle
} from 'lucide-react';
import ArticleCard from '../components/news/ArticleCard';
import { articleService } from '../services/api';
import { useReadingList } from '../context/AppContext';
import styles from './ArticlePage.module.css';

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isInReadingList, addToReadingList, removeFromReadingList } = useReadingList();
  
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const data = await articleService.getBySlug(slug);
      if (!data) {
        navigate('/404');
        return;
      }
      setArticle(data);

      // Cargar artículos relacionados
      const related = await articleService.getRelated(data.id, 4);
      setRelatedArticles(related);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article.title;

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleBookmark = () => {
    if (isInReadingList(article.id)) {
      removeFromReadingList(article.id);
    } else {
      addToReadingList(article);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    return minutes;
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Cargando artículo...</p>
      </div>
    );
  }

  if (!article) {
    return (
      <div className={styles.notFound}>
        <h1>Artículo no encontrado</h1>
        <Link to="/" className="btn btn-primary">Volver al inicio</Link>
      </div>
    );
  }

  const isSaved = isInReadingList(article.id);

  return (
    <article className={styles.article}>
      {/* Breadcrumb */}
      <nav className={styles.breadcrumb}>
        <div className="container">
          <Link to="/">Inicio</Link>
          <ChevronRight size={14} />
          <Link to={`/seccion/${article.category?.slug}`}>
            {article.category?.name}
          </Link>
          <ChevronRight size={14} />
          <span>{article.title.substring(0, 50)}...</span>
        </div>
      </nav>

      {/* Header del artículo */}
      <header className={styles.header}>
        <div className="container">
          <div className={styles.headerContent}>
            {/* Categoría */}
            <Link 
              to={`/seccion/${article.category?.slug}`}
              className={styles.category}
              style={{ backgroundColor: article.category?.color }}
            >
              {article.category?.name}
            </Link>

            {/* Título */}
            <h1 className={styles.title}>{article.title}</h1>

            {/* Subtítulo */}
            {article.subtitle && (
              <p className={styles.subtitle}>{article.subtitle}</p>
            )}

            {/* Extracto */}
            <p className={styles.excerpt}>{article.excerpt}</p>

            {/* Meta información */}
            <div className={styles.meta}>
              <div className={styles.metaLeft}>
                {/* Autor */}
                <div className={styles.author}>
                  {article.author?.avatar ? (
                    <img 
                      src={article.author.avatar} 
                      alt={article.author.name}
                      className={styles.authorAvatar}
                    />
                  ) : (
                    <span className={styles.authorAvatarFallback}>
                      {(article.author?.name || '?').charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className={styles.authorInfo}>
                    <Link 
                      to={`/autor/${article.author?.id}`}
                      className={styles.authorName}
                    >
                      {article.author?.name}
                    </Link>
                    {article.author?.role && (
                      <span className={styles.authorRole}>
                        {article.author.role}
                      </span>
                    )}
                  </div>
                </div>

                {/* Fecha y tiempo */}
                <div className={styles.dateTime}>
                  <span className={styles.date}>
                    <Calendar size={14} />
                    {formatDate(article.publishedAt)}
                  </span>
                  <span className={styles.time}>
                    <Clock size={14} />
                    {formatTime(article.publishedAt)}
                  </span>
                  <span className={styles.readTime}>
                    {calculateReadTime(article.content)} min de lectura
                  </span>
                </div>
              </div>

              <div className={styles.metaRight}>
                {/* Vistas */}
                <span className={styles.views}>
                  <Eye size={14} />
                  {article.views?.toLocaleString()} lecturas
                </span>

                {/* Acciones */}
                <div className={styles.actions}>
                  <button 
                    className={`${styles.actionBtn} ${isSaved ? styles.active : ''}`}
                    onClick={toggleBookmark}
                    title={isSaved ? 'Quitar de guardados' : 'Guardar artículo'}
                  >
                    {isSaved ? <BookmarkCheck size={18} /> : <Bookmark size={18} />}
                  </button>

                  <div className={styles.shareWrapper}>
                    <button 
                      className={styles.actionBtn}
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      title="Compartir"
                    >
                      <Share2 size={18} />
                    </button>

                    {showShareMenu && (
                      <div className={styles.shareMenu}>
                        <button onClick={() => handleShare('facebook')}>
                          <Facebook size={16} />
                          Facebook
                        </button>
                        <button onClick={() => handleShare('twitter')}>
                          <Twitter size={16} />
                          Twitter
                        </button>
                        <button onClick={() => handleShare('linkedin')}>
                          <Linkedin size={16} />
                          LinkedIn
                        </button>
                        <button onClick={copyLink}>
                          <LinkIcon size={16} />
                          {copied ? '¡Copiado!' : 'Copiar enlace'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Imagen principal */}
      {article.image && (
        <figure className={styles.featuredImage}>
          <div className="container">
            <img src={article.image} alt={article.title} />
            {article.imageCaption && (
              <figcaption>{article.imageCaption}</figcaption>
            )}
          </div>
        </figure>
      )}

      {/* Contenido del artículo */}
      <div className={styles.contentWrapper}>
        <div className="container">
          <div className={styles.contentGrid}>
            {/* Contenido principal */}
            <div 
              className={styles.content}
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Sidebar */}
            <aside className={styles.sidebar}>
              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <div className={styles.tagsSection}>
                  <h4>Etiquetas</h4>
                  <div className={styles.tags}>
                    {article.tags.map((tag, index) => (
                      <Link 
                        key={index} 
                        to={`/buscar?q=${encodeURIComponent(tag)}`}
                        className="tag"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Newsletter */}
              <div className={styles.newsletterBox}>
                <h4>Suscríbete</h4>
                <p>Recibe las noticias más importantes en tu correo</p>
                <form className={styles.newsletterForm}>
                  <input type="email" placeholder="Tu correo electrónico" />
                  <button type="submit" className="btn btn-primary">
                    Suscribirse
                  </button>
                </form>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {/* Artículos relacionados */}
      {relatedArticles.length > 0 && (
        <section className={styles.related}>
          <div className="container">
            <h3 className={styles.relatedTitle}>Artículos relacionados</h3>
            <div className={styles.relatedGrid}>
              {relatedArticles.map(relArticle => (
                <ArticleCard 
                  key={relArticle.id}
                  article={relArticle}
                  showExcerpt={false}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Navegación entre artículos */}
      <nav className={styles.articleNav}>
        <div className="container">
          <Link to="/" className={styles.navPrev}>
            <ChevronLeft size={20} />
            <span>Artículo anterior</span>
          </Link>
          <Link to="/" className={styles.navNext}>
            <span>Siguiente artículo</span>
            <ChevronRight size={20} />
          </Link>
        </div>
      </nav>
    </article>
  );
}
